// Shared types for composer components

export type ContentType = 'post' | 'story' | 'reel' | 'igtv' | 'carousel' | 'short';

// Story sticker types supported by Instagram / Facebook Graph API.
// Only ``link`` is auto-publishable; the others require notification
// mode (user finishes manually in the native app).
export type StoryStickerType =
  | 'link'
  | 'poll'
  | 'question'
  | 'quiz'
  | 'slider'
  | 'location'
  | 'mention'
  | 'hashtag'
  | 'countdown';

export interface StorySticker {
  type: StoryStickerType;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
  url?: string;
  label?: string;
  question?: string;
  options?: string[];
  correct_answer_index?: number;
  emoji?: string;
  target?: string;
  end_time?: string;
}

export interface StoryCard {
  index: number;
  media_index?: number;
  copy?: string;
  background_color?: string;
  stickers: StorySticker[];
}

export interface ReelScriptBeat {
  t: string;
  line: string;
  overlay?: string;
  duration_seconds?: number;
}

export interface CarouselSlide {
  index: number;
  media_index?: number;
  title?: string;
  body?: string;
}

export interface Platform {
  id: string;
  name: string;
  maxChars: number;
  icon: string;
  color: string;
  supportsLinkPreview: boolean;
  supportsFirstComment: boolean;
  supportsAltText: boolean;
  supportsVideo: boolean;
  supportsCarousel: boolean;
  /** Content types this platform can publish natively or via mapping */
  supportedContentTypes: ContentType[];
  /** Content types that get silently mapped to another type (e.g. reel → tweet) */
  contentTypeMappings?: Partial<Record<ContentType, string>>;
}

export interface MediaFile {
  id: string;
  file: File;
  preview: string;
  type: 'image' | 'video';
  altText?: string;
  /** Server-side media id. Empty until the file has been uploaded at
   *  least once (either via the AI-describe action or via the
   *  Save/Publish flow). When present, the final save skips re-upload
   *  and references this id directly, avoiding duplicate storage. */
  mediaId?: string;
}

export interface UrlMedia {
  id: string;
  url: string;
  filename: string;
  contentType: string;
  type: 'image' | 'video';
  status: 'pending' | 'uploading' | 'done' | 'error';
  progress: number;
  loaded?: number;
  total?: number;
  mediaId?: string;
  error?: string;
  altText?: string;
  platformVariants?: Record<string, string>;
}

export interface LinkPreview {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  site_name?: string;
  favicon?: string;
  domain?: string;
  link_type?: string;
}

export interface CloudFile {
  id: string;
  name: string;
  mime_type: string;
  size: number;
  thumbnail?: string;
  modified_at?: string;
  type: 'image' | 'video';
  path_lower?: string;
}

export interface CloudAccount {
  id: string;
  provider: string;
  email: string;
  display_name: string;
}

export interface ConnectedAccount {
  id: string;
  platform: string;
  username: string;
  display_name: string;
  avatar_url: string;
  profile_url: string;
  is_active: boolean;
  last_synced: string | null;
}

export interface ScheduleConfig {
  scheduleType: 'now' | 'later';
  scheduledAt?: Date;
}

// Platform configuration with capability flags
export const PLATFORMS: Platform[] = [
  {
    id: 'instagram', name: 'Instagram', maxChars: 2200, icon: '📷', color: 'bg-pink-500',
    supportsLinkPreview: false, supportsFirstComment: true, supportsAltText: true, supportsVideo: true, supportsCarousel: true,
    supportedContentTypes: ['post', 'story', 'reel', 'igtv', 'carousel'],
    contentTypeMappings: { igtv: 'Reel' },
  },
  {
    id: 'facebook', name: 'Facebook', maxChars: 63206, icon: '👍', color: 'bg-blue-600',
    supportsLinkPreview: true, supportsFirstComment: false, supportsAltText: true, supportsVideo: true, supportsCarousel: true,
    supportedContentTypes: ['post', 'story', 'reel', 'carousel'],
  },
  {
    id: 'twitter', name: 'Twitter/X', maxChars: 280, icon: '𝕏', color: 'bg-black',
    supportsLinkPreview: true, supportsFirstComment: false, supportsAltText: true, supportsVideo: true, supportsCarousel: true,
    supportedContentTypes: ['post', 'reel'],
    contentTypeMappings: { reel: 'Video tweet' },
  },
  {
    // TikTok: added `story` (P0-7, backend platform_specs entry for
    // (tiktok, story) landed) and `carousel` (Photo Mode 2–35 slides).
    id: 'tiktok', name: 'TikTok', maxChars: 2200, icon: '♪', color: 'bg-gray-900',
    supportsLinkPreview: false, supportsFirstComment: false, supportsAltText: false, supportsVideo: true, supportsCarousel: true,
    supportedContentTypes: ['post', 'reel', 'story', 'carousel'],
    contentTypeMappings: { post: 'Video', reel: 'Video' },
  },
  {
    id: 'linkedin', name: 'LinkedIn', maxChars: 3000, icon: '💼', color: 'bg-blue-700',
    supportsLinkPreview: true, supportsFirstComment: true, supportsAltText: true, supportsVideo: true, supportsCarousel: true,
    supportedContentTypes: ['post', 'carousel'],
    contentTypeMappings: { reel: 'Video post' },
  },
  {
    id: 'youtube', name: 'YouTube', maxChars: 5000, icon: '▶', color: 'bg-red-600',
    supportsLinkPreview: false, supportsFirstComment: false, supportsAltText: false, supportsVideo: true, supportsCarousel: false,
    supportedContentTypes: ['post', 'reel'],
    contentTypeMappings: { reel: 'Short' },
  },
  {
    id: 'pinterest', name: 'Pinterest', maxChars: 500, icon: '📌', color: 'bg-red-600',
    supportsLinkPreview: true, supportsFirstComment: false, supportsAltText: true, supportsVideo: true, supportsCarousel: false,
    supportedContentTypes: ['post'],
    contentTypeMappings: { reel: 'Video pin' },
  },
  {
    // Threads: P0-7 added dedicated `(threads, carousel)` spec (2-10
    // slides) on the backend. Expose the content type here so the
    // picker surfaces it.
    id: 'threads', name: 'Threads', maxChars: 500, icon: '@', color: 'bg-gray-800',
    supportsLinkPreview: true, supportsFirstComment: false, supportsAltText: true, supportsVideo: true, supportsCarousel: true,
    supportedContentTypes: ['post', 'reel', 'carousel'],
    contentTypeMappings: { reel: 'Reel' },
  },
  {
    // Bluesky: P0-7 expanded the spec to include video (100 MB /
    // 60 s / h264). Flip the flag + allow reel content-type.
    id: 'bluesky', name: 'Bluesky', maxChars: 300, icon: '☁️', color: 'bg-blue-400',
    supportsLinkPreview: true, supportsFirstComment: false, supportsAltText: true, supportsVideo: true, supportsCarousel: false,
    supportedContentTypes: ['post', 'reel'],
  },
];
